import type { RoundResult as RR } from '../../../worker/lib/scoring'
import { MAX_ROUND_SCORE } from '../../../worker/lib/scoring'
import { useT } from '../i18n'
import { fmt, fmtYear } from '../lib/format'

type Props = {
  result: RR
  answer: {
    year: number
    year_min: number
    year_max: number
    place_name_en: string
    place_name_ar: string
    place_name_historical: string | null
    caption_en: string
    caption_ar: string
    attribution_text: string | null
    source_url: string
  }
  guess: { year: number }
  onNext: () => void
  isLast: boolean
}

export default function RoundResult({ result, answer, guess, onNext, isLast }: Props) {
  const { t, locale } = useT()
  const placeName = locale === 'ar' && answer.place_name_ar ? answer.place_name_ar : answer.place_name_en
  const caption = locale === 'ar' && answer.caption_ar ? answer.caption_ar : answer.caption_en

  const yearBracketStr = answer.year_min === answer.year_max
    ? fmtYear(answer.year, locale)
    : `${fmtYear(answer.year, locale)} (${fmtYear(answer.year_min, locale)}–${fmtYear(answer.year_max, locale)})`

  return (
    <div className="bg-cream-50 border border-cream-200 rounded-lg p-4 sm:p-5 space-y-4 shadow-sm text-earth-900">
      <div className="text-sm">
        <div className="text-earth-700/70 text-xs uppercase tracking-wide font-sans">
          {t('play.year')}
        </div>
        <div className="font-mono text-xs sm:text-sm">
          <strong>{fmtYear(guess.year, locale)}</strong> · <strong>{yearBracketStr}</strong>
          {result.year_delta > 0 && (
            <span className="text-earth-700/60"> · {t('play.off_by_n_years', { n: fmt(result.year_delta, locale) })}</span>
          )}
        </div>
      </div>

      <div className="text-3xl font-bold text-center py-2 border-y border-cream-200 tabular-nums">
        +{fmt(result.total, locale)}{' '}
        <span className="text-sm text-earth-700/60 font-normal">
          {t('play.of_max', { max: fmt(MAX_ROUND_SCORE, locale) })}
        </span>
      </div>

      <div className="text-sm text-earth-800">
        <span className="text-earth-700/70 text-xs uppercase tracking-wide font-sans me-2">
          {t('play.place')}
        </span>
        <strong>{placeName}</strong>
        {answer.place_name_historical && (
          <span className="text-earth-700/70"> · <em>{answer.place_name_historical}</em></span>
        )}
      </div>

      <div className="text-sm text-earth-800 font-body leading-relaxed">{caption}</div>

      <div className="text-xs text-earth-700/60 flex justify-between items-baseline gap-2">
        <span className="truncate">{answer.attribution_text}</span>
        <a href={answer.source_url} target="_blank" rel="noreferrer" className="underline whitespace-nowrap hover:text-sand-700">
          {t('play.source')}
        </a>
      </div>

      <button
        onClick={onNext}
        className="w-full bg-earth-800 text-cream-50 rounded py-3 font-semibold touch-manipulation hover:bg-sand-600 transition"
      >
        {isLast ? t('play.see_total') : t('play.next_photo')}
      </button>
    </div>
  )
}
